// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { SearchEmployeeBySurname } from "./search-employee-by-surname";
import dataActionCreators from "@modules/data/actionCreator";
import employeeActionCreators from "@modules/employee/actionCreator";
import * as selectors from "@modules/employee/selectors";
import type { Employee } from "@modules/employee/reducer/employee/types";
import type { AlertBespokeRecipient } from "@modules/service/reducer/alerts/types";

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  LinearWizard,
  Step,
} from "@components";
import type { State } from "@store/reducers";
import { selectActiveHierarchyStructure } from "@modules/data/selectors";
import { HierarchyScopeForm } from "@modules/service/components/hierarchy-scope";
import * as serviceSelectors from "@modules/service/selectors";

type OwnProps = $Exact<{
  clientId: number,
  onCancel: () => void,
  onSubmit: (bespoke: AlertBespokeRecipient) => void,
}>;

type PropsFromState = $Exact<{
  employeeResults: ?Array<Employee>,
  hierarchyTree: Object | null,
  bespokeRecipient: Object | null,
}>;

type PropsFromDispatch = $Exact<{
  searchEmployees: typeof employeeActionCreators.SearchEmployees,
  getActiveHierarchy: typeof dataActionCreators.GetActiveHierarchy,
  selectBespokeRecipient: typeof employeeActionCreators.SelectBespokeRecipient,
}>;
type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;
type OwnState = $Exact<{
  employee: Object,
  assignmentRules: Array<Object>,
}>;

export class AlertAddBespokeRecipientModal extends React.Component<Props, OwnState> {
  state = {
    employee: {},
    assignmentRules: [],
  };

  onWizardChange = (wizardData: Array<Object> = []) => {
    const assignmentRules = wizardData[1] && wizardData[1].length ? wizardData[1] : [];
    this.setState({
      employee: wizardData[0].id ? wizardData[0] : this.state.employee,
      // $FlowFixMe Second step can't be Array ?
      assignmentRules,
    });
  };
  componentDidMount() {
    this.props.getActiveHierarchy({
      clientId: this.props.clientId,
      includeEmployeePositions: false,
    });
  }
  componentDidUpdate() {
    if (this.props.bespokeRecipient) {
      this.setState({
        employee: this.props.bespokeRecipient,
        assignmentRules: this.props.bespokeRecipient.assignmentRules,
      });
      this.props.selectBespokeRecipient(null);
    }
  }

  submitForm = () => {
    const selectedEmployee = this.state.employee;

    this.props.onSubmit({
      id: selectedEmployee.id,
      employeeRefPrimary: selectedEmployee.employeeRefPrimary,
      firstName: selectedEmployee.firstName,
      lastName: selectedEmployee.lastName,
      name: selectedEmployee.name,
      workEmail: selectedEmployee.workEmail || selectedEmployee.contactDetails?.workEmail,
      assignmentRules: this.state.assignmentRules,
    });
    this.setState({
      employee: {},
      assignmentRules: [],
    });
  };
  onSelectEmployee = (employee: Employee) => {
    this.setState({ employee });
  };
  hierarchyScopeForm = (props: Object) =>
    props.editable ? <HierarchyScopeForm {...props} /> : null;

  toggleModal = () => {
    this.setState({
      employee: {},
      assignmentRules: [],
    });
    this.props.onCancel();
  };
  render() {
    const employeeData = this.props.bespokeRecipient || this.state.employee;
    const assignmentRules =
      (this.props.bespokeRecipient || {}).assignmentRules || this.state.assignmentRules;
    return (
      <Modal className="modal-large">
        <ModalHeader className="d-flex justify-content-between" toggle={this.toggleModal}>
          {this.props.t("ALERT_DETAILS_ADD_BESPOKE_RECIPIENT")}
        </ModalHeader>
        <ModalBody>
          <LinearWizard
            data={[]}
            defaultStep={!employeeData.id ? 1 : 2}
            onChange={this.onWizardChange}
          >
            <Step
              key="search-employee"
              component={SearchEmployeeBySurname}
              searchEmployees={this.props.searchEmployees}
              clientId={this.props.clientId}
              t={this.props.t}
              employeeResults={this.props.employeeResults}
              title={this.props.t("STEP_1_OF_2_SELECT_EMPLOYEE")}
              selectEmployee={this.onSelectEmployee}
              selectedEmployee={employeeData}
            />
            <Step
              key="hierarchy-scope"
              component={this.hierarchyScopeForm}
              title={this.props.t("STEP_2_OF_2_HIERARCHY_SCOPE")}
              hierarchy={this.props.hierarchyTree}
              assignments={assignmentRules}
            />
          </LinearWizard>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={this.toggleModal}>
            {this.props.t("CANCEL")}
          </Button>
          <Button
            materialIcon="save"
            disabled={!this.state.employee.id === null || this.state.assignmentRules.length < 1}
            onClick={this.submitForm}
          >
            {this.props.t("SAVE")}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

const mapStateToProps = (state: State): PropsFromState => ({
  employeeResults: selectors.getEmployees(state),
  hierarchyTree: selectActiveHierarchyStructure(state),
  bespokeRecipient: serviceSelectors.getBespokeRecipient(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  searchEmployees: employeeActionCreators.GetEmployees,
  getActiveHierarchy: dataActionCreators.GetActiveHierarchy,
  selectBespokeRecipient: employeeActionCreators.SelectBespokeRecipient,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AlertAddBespokeRecipientModal);
